import type Phaser from "phaser";
import { AVATAR_IDS, type Facing } from "@zg/shared";
import { artUrl } from "../art/asset.js";

/**
 * The people in the venue, from authored art.
 *
 * A man in a white kurta and dhoti with a red gamosa, and a woman in a
 * mekhela sador — what you wear to this, and what most of the hall will be
 * wearing on the night. Each arrives as eight sheets, one per animation per
 * facing, drawn in several passes and agreeing about almost nothing: six
 * frames or ten, three canvas sizes, three ways of saying "background".
 * `scripts/extract-man.mjs` turns them into the grid loaded here: one fixed
 * cell, every frame centred on the head and standing on the same line.
 *
 * Three colours each, and they are real colours, not shades. This started as
 * four near-white men and became six figures nobody could tell apart at forty
 * pixels tall, which is the size they are actually seen at. Cloth is the only
 * thing with enough area to carry a difference, so cloth is what changes.
 *
 * The two characters need opposite treatments, because their garments are
 * opposite. His kurta and dhoti are near-white, so a MULTIPLY puts any colour
 * on them and keeps every fold — and it leaves the red of the gamosa alone,
 * which matters, because the red on a white gamosa is the point of what he is
 * wearing. Her mekhela sador is already cream and red, so a multiply found
 * nothing to work on at all and shipped three identical women; hers is a HUE
 * ROTATION over the garment's rows instead, which moves the colour and leaves
 * the weave, the motifs and her skin where they were.
 *
 * Re-run when the art changes:
 *
 * The man's `--steps-right` is not optional, and it is deliberately only the
 * RIGHT. His two side sheets were drawn in different passes and do not agree.
 * The right runs contact, down, pass, contact, PASS, CONTACT — its second
 * half-step ends on a contact, so frames six and one are both wide stances and
 * the loop plants the same foot twice, which reads as a limp. The left is
 * already a valid cycle; it simply starts mid-step on the down pose rather
 * than the contact, and a loop does not care where it begins. Reordering both
 * fixes the right by breaking the left.
 *
 *   node client/scripts/extract-man.mjs man   client/public/art/avatar1.png        --steps-right=0,1,2,3,5,4
 *   node client/scripts/extract-man.mjs man   client/public/art/avatar2.png e0a040 --steps-right=0,1,2,3,5,4
 *   node client/scripts/extract-man.mjs man   client/public/art/avatar3.png 3f8f86 --steps-right=0,1,2,3,5,4
 *   node client/scripts/extract-man.mjs woman client/public/art/avatar4.png
 *   node client/scripts/extract-man.mjs woman client/public/art/avatar5.png --hue=210
 *   node client/scripts/extract-man.mjs woman client/public/art/avatar6.png --hue=140
 */

export const FRAME_W = 40;
export const FRAME_H = 56;

/**
 * Columns in the sheet, and how many of them each animation fills.
 *
 * The grid is as wide as the longest animation, so the four idle rows leave
 * their last two cells empty. These counts must match `STRIPS` in
 * `scripts/extract-man.mjs`, which is what wrote the sheet and which warns if
 * a redrawn sheet stops agreeing with them.
 *
 * Six frames is one full walk cycle, heel to the same heel. Idle ships the
 * four near-identical standing frames of its sheet and drops the two big pose
 * frames, because every avatar in the venue shares one animation clock and a
 * pose frame there is three hundred people folding their arms in unison.
 */
const COLS = 6;
const WALK_STEPS = 6;
const IDLE_STEPS = 4;

/**
 * How long one walk cycle takes, heel to the same heel.
 *
 * Held fixed and the frame rate derived from it, so that a sheet redrawn at a
 * different frame count keeps the same gait instead of striding faster or
 * slower for it.
 *
 * It would be better to derive this from `PLAYER_SPEED` and the stride the art
 * draws, so the feet keep pace with the floor instead of skating over it. That
 * is not possible with these sheets: measured at full resolution, the
 * side-facing frames lift a foot at most 6px on a 413px figure — 1.5% of his
 * height, where a walk needs 8-12% — and the rear foot barely leaves its spot
 * all cycle. There is no stride in the art to match a speed to. Until there
 * is, this is a cadence chosen to read calmly, not a derived number.
 */
const WALK_CYCLE_MS = 460;

/** Row order in the extracted sheet. Frame index = row * COLS + step. */
const WALK_ROW: Record<Facing, number> = { down: 0, left: 1, right: 2, up: 3 };
const IDLE_ROW: Record<Facing, number> = { down: 4, left: 5, right: 6, up: 7 };

/**
 * Kept so `PlayerView` and the pose code keep a single name for "which row is
 * this facing". Walk and idle share the ordering; only the offset differs.
 */
export const FACING_ROW = WALK_ROW;

/** The frame someone standing still is showing. */
export function idleFrame(facing: Facing): number {
  return IDLE_ROW[facing] * COLS;
}

/** Queue every avatar sheet. Call from a scene `preload`. */
export function loadCharacters(scene: Phaser.Scene): void {
  for (const avatarId of AVATAR_IDS) {
    scene.load.spritesheet(avatarId, artUrl(`art/${avatarId}.png`), {
      frameWidth: FRAME_W,
      frameHeight: FRAME_H,
    });
  }
}

/**
 * Build the walk and idle animations for every avatar.
 *
 * Idle is a real four-frame loop rather than a held frame: the character
 * breathes, and in a room of people standing at a vigil that small motion is
 * the difference between a crowd and a set of statues.
 */
export function createCharacterAnims(scene: Phaser.Scene): void {
  const facings = Object.keys(WALK_ROW) as Facing[];

  for (const avatarId of AVATAR_IDS) {
    if (!scene.textures.exists(avatarId)) continue;

    for (const facing of facings) {
      const walk = `${avatarId}-walk-${facing}`;
      if (!scene.anims.exists(walk)) {
        scene.anims.create({
          key: walk,
          frames: frameRange(avatarId, WALK_ROW[facing], WALK_STEPS),
          frameRate: (WALK_STEPS * 1000) / WALK_CYCLE_MS,
          repeat: -1,
        });
      }

      const idle = `${avatarId}-idle-${facing}`;
      if (!scene.anims.exists(idle)) {
        scene.anims.create({
          key: idle,
          frames: frameRange(avatarId, IDLE_ROW[facing], IDLE_STEPS),
          // Slow: a breath, not a fidget.
          frameRate: 3,
          repeat: -1,
        });
      }
    }
  }
}

function frameRange(
  avatarId: string,
  row: number,
  steps: number,
): Phaser.Types.Animations.AnimationFrame[] {
  return Array.from({ length: steps }, (_, step) => ({
    key: avatarId,
    frame: row * COLS + step,
  }));
}

/**
 * Draw one idle frame into a canvas, for the DOM avatar picker on the join
 * screen.
 *
 * The picker paints before Phaser has loaded anything, so this fetches the
 * sheet itself and fills the canvas when it arrives. The canvas is returned
 * immediately and empty; a picker that waits for four images before it renders
 * is a join screen that looks broken on a slow connection.
 */
export function renderAvatarPreview(avatarIndex: number, scale = 2): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = FRAME_W * scale;
  canvas.height = FRAME_H * scale;
  const ctx = canvas.getContext("2d");
  const avatarId = AVATAR_IDS[avatarIndex % AVATAR_IDS.length];
  if (!ctx || !avatarId) return canvas;

  const image = new Image();
  image.decoding = "async";
  image.src = artUrl(`art/${avatarId}.png`);
  image.addEventListener("load", () => {
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(
      image,
      0,
      IDLE_ROW.down * FRAME_H,
      FRAME_W,
      FRAME_H,
      0,
      0,
      FRAME_W * scale,
      FRAME_H * scale,
    );
  });

  return canvas;
}
